import { useEffect, useRef, useSyncExternalStore } from "react";
import ConfirmModal from "components/ui/ConfirmModal";
import useBodyScrollLock from "hooks/useBodyScrollLock";

/**
 * 작성 화면 공용 '저장하지 않고 나가기' 가드.
 *  - 각 작성 화면: useUnsavedGuard({ form, images, ... }) 한 줄만 호출(감시할 상태 묶음 전달).
 *  - 모달 UI 는 DefaultLayout 의 <UnsavedGuardHost/> 단일 인스턴스가 렌더.
 *  - 헤더 뒤로가기(DefaultLayout.onBack)가 requestBackGuard() 로 가로챔.
 *  - 프리필 흡수: 첫 사용자 입력(pointerdown/keydown) 전의 상태 변화는 비동기 로드로 보고
 *    스냅샷을 갱신 — 수정모드에서 데이터만 불러오고 나가도 모달이 안 뜬다.
 *  - 임시저장(선택): useUnsavedGuard(value, enabled, { onSaveDraft, onDiscardDraft, restored })
 *    onSaveDraft 를 넘긴 화면은 앱과 같은 3지선다 확인창을 띄운다
 *    '작성 중인 내용을 저장할까요?' [임시저장 후 나가기] [저장 안 하고 나가기] [계속 작성].
 *    restored=true(임시저장을 불러온 화면)는 입력 전이라도 작성 중으로 본다 — 그냥 나가도 확인창이 뜨게.
 *    보통은 hooks/useWriteDraft 가 돌려주는 guard 값을 그대로 넘긴다.
 */

// ── 모듈 스토어(SPA 단일) ──
let activeGuard = null; // dirty 한 화면이 등록한 이탈 가드
let leaving = false;    // '나가기' 확정 — 센티널 pop 연쇄가 모달을 재소환하지 않게
let modalState = { open: false, proceed: null, draft: null };
const CLOSED = { open: false, proceed: null, draft: null };
const listeners = new Set();
const emit = () => listeners.forEach((l) => l());
const setModal = (next) => { modalState = next; emit(); };

// ── 작성 화면 위 전체화면 오버레이(미리보기 등) ──
//  오버레이는 자기 히스토리 항목을 쌓고 뒤로가기로 닫힌다. 그 pop 은 작성 화면 이탈이 아니므로
//  가드가 확인창을 띄우면 안 된다(2026-10-22 미리보기 ‹ 뒤 '저장하지 않고 나갈까요?' 문제).
//  닫힘 표시는 다음 tick 에 내린다 — 같은 popstate 를 가드가 처리하는 동안엔 아직 열린 것으로 보이게.
let overlayDepth = 0;
const overlayListeners = new Set();
export const enterGuardOverlay = () => { overlayDepth += 1; };
export const leaveGuardOverlay = () => {
  setTimeout(() => {
    overlayDepth = Math.max(0, overlayDepth - 1);
    if (overlayDepth === 0) overlayListeners.forEach((l) => l());
  }, 0);
};

// 헤더 뒤로가기에서 호출 — 가드가 있으면 확인 모달을 띄우고 true(이동 보류) 반환.
// draft = 화면이 넘긴 임시저장 콜백({ onSaveDraft, onDiscardDraft }) — 있으면 3지선다 확인창.
const draftOf = (opts) => (opts && typeof opts.onSaveDraft === "function"
  ? { onSaveDraft: opts.onSaveDraft, onDiscardDraft: opts.onDiscardDraft }
  : null);

export const requestBackGuard = (proceed) => {
  if (!activeGuard) return false;
  setModal({ open: true, proceed, draft: draftOf(activeGuard.optsRef?.current) });
  return true;
};

// 화면 내 자체 취소/이동 버튼용 — dirty 면 확인 후 실행, 아니면 즉시 실행.
export const confirmUnsavedThen = (fn) => {
  if (!requestBackGuard(fn)) fn();
};

// 제출 전 초안을 저장한 뒤 가입·로그인 화면으로 이동할 때 사용한다.
// 현재 작성 화면의 센티널을 억지로 걷어내지 않아, 가입을 취소하면 같은 작성 화면으로 돌아올 수 있다.
export const allowUnsavedNavigation = () => {
  activeGuard = null;
  leaving = true;
};

// ── 화면 훅 ──
export default function useUnsavedGuard(value, enabled = true, options = null) {
  const armedRef = useRef(false);     // 첫 사용자 입력 발생 여부
  const snapRef = useRef("");
  const dirtyRef = useRef(false);     // 리스너가 최신 dirty 를 보게(리스너는 마운트 1회 등록)
  const sentinelRef = useRef(false);  // 우리가 push 한 센티널이 살아 있는가
  const ignorePopRef = useRef(false); // 우리가 만든 back() 으로 생기는 pop 1회 무시
  const guardRef = useRef(null);
  const optsRef = useRef(options);    // 최신 임시저장 콜백(모달이 열릴 때 읽는다)
  optsRef.current = options;

  const json = JSON.stringify(value ?? null);
  if (!armedRef.current) snapRef.current = json; // 입력 전 변화 = 프리필 → 기준 갱신
  // 임시저장을 불러온 화면은 입력 전이라도 작성 중 — 불러온 내용이 기준(스냅샷)에 흡수돼도 나갈 때 묻는다.
  const dirty = enabled && (Boolean(options?.restored) || (armedRef.current && json !== snapRef.current));
  dirtyRef.current = dirty;

  useEffect(() => {
    const arm = () => { armedRef.current = true; };
    window.addEventListener("pointerdown", arm, { capture: true, once: true });
    window.addEventListener("keydown", arm, { capture: true, once: true });
    return () => {
      window.removeEventListener("pointerdown", arm, { capture: true });
      window.removeEventListener("keydown", arm, { capture: true });
    };
  }, []);

  /**
   * popstate 리스너는 **마운트당 1회만** 등록한다.
   *
   * ⚠️ 2026-08-11 수정 — 종전에는 이 리스너를 `[dirty]` 이펙트 안에서 등록/해제했고,
   *  정리(cleanup)에서 `history.back()` 을 불렀다. back() 은 비동기라서
   *  dirty 가 한 번이라도 토글되면(비동기 프리필이 입력 직후 도착하는 경우 등)
   *  → 정리에서 back() 예약 → dirty 재개로 리스너 재등록 → 예약됐던 pop 을 **새 리스너가 받아**
   *  사용자가 뒤로가기를 누르지도 않았는데 "저장하지 않고 나가시겠습니까?" 가 떴다.
   *  (공고/대타 등록에서 한 글자만 입력해도 모달이 뜨던 현상)
   *
   *  리스너를 고정하고, 우리가 만든 back() 은 ignorePopRef 로 1회 무시한다.
   *  이제 모달은 **실제 뒤로가기(헤더/스와이프/하드웨어)** 에서만 뜬다.
   */
  useEffect(() => {
    const onPop = (event) => {
      // 우리 정리 코드가 만든 pop — 화면 이탈 의도가 아니므로 조용히 흘린다.
      if (ignorePopRef.current) {
        ignorePopRef.current = false;
        sentinelRef.current = false;
        return;
      }
      // 미리보기 같은 중첩 전체화면이 열려 있거나 막 닫히는 중 — 그 화면을 닫는 뒤로가기다.
      if (overlayDepth > 0) return;
      // 중첩 전체화면이 닫혀 작성 화면의 센티널로 돌아온 경우다.
      // 작성 화면 자체의 뒤로가기가 아니므로 이탈 확인창을 띄우지 않는다.
      if (event.state?.muyeonUnsavedSentinel) return;
      if (leaving) {
        // dirty 상태에서 만든 센티널을 먼저 소모한 경우에만 실제 엔트리까지 한 번 더 이동한다.
        // 센티널이 없는 저장 완료/복원 초안 화면에서 무조건 back() 하면 이전 화면도 건너뛴다.
        if (sentinelRef.current) {
          sentinelRef.current = false;
          window.history.back();
        }
        return;
      }
      if (!dirtyRef.current) { sentinelRef.current = false; return; }  // 변경 없음 → 그대로 이탈

      window.history.pushState({ muyeonUnsavedSentinel: true }, "");   // 센티널 복원
      sentinelRef.current = true;
      setModal({
        open: true,
        proceed: () => window.history.back(),
        draft: draftOf(optsRef.current),
      });
    };
    window.addEventListener("popstate", onPop);
    // 오버레이가 열린 동안 dirty 가 된 경우 센티널을 미뤄 뒀다가, 닫힌 뒤 작성 화면 위에 쌓는다.
    const onOverlayClosed = () => {
      if (!dirtyRef.current || sentinelRef.current || leaving) return;
      window.history.pushState({ muyeonUnsavedSentinel: true }, "");
      sentinelRef.current = true;
    };
    overlayListeners.add(onOverlayClosed);

    return () => {
      window.removeEventListener("popstate", onPop);
      overlayListeners.delete(onOverlayClosed);
      if (sentinelRef.current && !leaving) {
        // 리스너는 이미 떼었지만, 다른 화면의 가드가 이 pop 을 받지 않도록 표시해 둔다.
        ignorePopRef.current = true;
        window.history.back();
      }
      sentinelRef.current = false;
    };
  }, []);

  /**
   * 센티널 관리 + 헤더 뒤로가기용 가드 등록.
   * 스와이프백/브라우저 뒤로가기 대비로 dirty 동안 동일 URL 센티널을 <b>1개만</b> 유지한다
   * (같은 URL이라 화면은 그대로, 뒤로가기는 센티널만 소모 → 모달로 가로챔).
   */
  useEffect(() => {
    if (dirty) {
      const g = { optsRef };
      guardRef.current = g;
      activeGuard = g;
      // 미리보기 위에 센티널을 쌓으면 미리보기를 닫아도 그 항목이 남아 다음 뒤로가기가 꼬인다 — 닫힌 뒤로 미룬다.
      if (!sentinelRef.current && overlayDepth === 0) {
        leaving = false;
        window.history.pushState({ muyeonUnsavedSentinel: true }, "");
        sentinelRef.current = true;
      }
      return () => { if (activeGuard === g) activeGuard = null; };
    }

    // 변경이 사라졌으면(저장 성공/원복) 센티널을 걷어낸다 — 남으면 뒤로가기 한 번이 헛돈다.
    if (activeGuard === guardRef.current) activeGuard = null;
    if (sentinelRef.current && !leaving) {
      ignorePopRef.current = true;
      window.history.back();
      sentinelRef.current = false;
    }
    return undefined;
  }, [dirty]);
}

// ── 모달 호스트(DefaultLayout 에서 1개 렌더) ──
//  임시저장 콜백이 있는 화면은 앱(JobPostingWizardView·LessonWizardView)과 같은 3지선다,
//  없는 화면은 종전 2버튼 확인창. 3지선다는 새 CSS 없이 공용 바텀시트(modal-bottom-dimmed + btn-group flex-col).
export function UnsavedGuardHost() {
  const state = useSyncExternalStore(
    (cb) => { listeners.add(cb); return () => listeners.delete(cb); },
    () => modalState,
  );
  const close = () => setModal(CLOSED);
  const leave = (before) => {
    const p = state.proceed;
    setModal(CLOSED);
    if (before) { try { before(); } catch (e) { /* 저장 실패여도 나가기는 진행 */ } }
    activeGuard = null; // 나가기 확정 — 가드 해제 후 이동
    leaving = true;     // 이후 popstate(센티널 소모)는 이탈 연쇄로 처리
    if (p) p();
  };
  const draft = state.draft;
  return (
    <>
      <ConfirmModal
        isShow={state.open && !draft}
        title="저장하지 않고 나갈까요?"
        body="수정한 내용은 저장되지 않아요."
        cancelText="계속 수정하기"
        confirmText="나가기"
        onCancel={close}
        onConfirm={() => leave(null)}
      />
      <DraftLeaveSheet
        isShow={state.open && !!draft}
        onSave={() => leave(draft?.onSaveDraft)}
        onDiscard={() => leave(draft?.onDiscardDraft)}
        onCancel={close}
      />
    </>
  );
}

function DraftLeaveSheet({ isShow, onSave, onDiscard, onCancel }) {
  useBodyScrollLock(isShow);
  return (
    <div data-id="unsaved-draft-sheet" className={"modal-bottom-dimmed" + (isShow ? " is-show" : "")}>
      <div className="modal-bottom">
        <div className="tit-box">
          <p>작성 중인 내용을 저장할까요?</p>
        </div>
        <div className="con-box">
          <p className="t-side" style={{ wordBreak: "keep-all" }}>
            임시저장하면 다음에 들어올 때 이어서 쓸 수 있어요.
          </p>
        </div>
        <div className="btn-group flex-col">
          <button type="button" className="btn-md filled primary" onClick={onSave}>임시저장 후 나가기</button>
          <button type="button" className="btn-md outlined" onClick={onDiscard}>저장 안 하고 나가기</button>
          <button type="button" className="btn-md" onClick={onCancel}>계속 작성</button>
        </div>
      </div>
    </div>
  );
}
